import { MENTOR_CAFE_NAV_COPY } from "src/shared/constants/mentorCafe";

export type MentorCafeInfoCardId =
  | "location"
  | "hours"
  | "walk-in"
  | "conversation";

export type MentorCafeCopy = Readonly<{
  text: string;
  status: "final" | "placeholder";
}>;

const finalCopy = (text: string): MentorCafeCopy => ({
  text,
  status: "final",
});

const placeholderCopy = (text: string): MentorCafeCopy => ({
  text,
  status: "placeholder",
});

export type MentorCafeInfoCard = {
  id: MentorCafeInfoCardId;
  title: MentorCafeCopy;
  body: MentorCafeCopy;
};

export type MentorCafePlaceholder = {
  id: string;
  name: MentorCafeCopy;
  title: MentorCafeCopy;
};

export type MentorCafeRotation = {
  id: string;
  title: MentorCafeCopy;
  time: MentorCafeCopy;
  description: MentorCafeCopy;
  mentors: MentorCafePlaceholder[];
};

/**
 * Confirmed copy supplied in the Mentor Cafe Notion spec is marked final.
 * Suggested copy, schedule details, and mentor profiles remain placeholders
 * until they are confirmed or production data is available.
 */
export const MENTOR_CAFE_COPY = {
  headerTitle: MENTOR_CAFE_NAV_COPY,
  headerSubtitle: placeholderCopy(
    "A cozy corner for coffee, conversation, and career advice."
  ),
  introTitle: finalCopy("The Mentorship Cafe is open 💙"),
  introBody: finalCopy(
    "Have an actual coffee chat at Hack the North’s Third Space. We've turned C&D into a cozy corner with real drinks and real mentors. Come by to get unstuck on your project, get advice from founders and VCs, ask for advice on your portfolio from designers, or just ask the career questions you've been sitting on."
  ),
  mentorLineupStatus: placeholderCopy("Mentor lineup coming soon."),
};

export const MENTOR_CAFE_INFO_CARDS: MentorCafeInfoCard[] = [
  {
    id: "location",
    title: finalCopy("Where is the Cafe?"),
    body: finalCopy(
      "Find us at C&D on the first floor of PSE (formerly E7). Look for the standing tables and the coffee. Pop by anytime for some extra caffeine or to meet some fellow hackers!"
    ),
  },
  {
    id: "hours",
    title: finalCopy("When are mentors around?"),
    body: finalCopy(
      "Mentors are in the Cafe all day Saturday, 10AM to 8PM. The space itself stays open the rest of the weekend to sit, chat, and recharge."
    ),
  },
  {
    id: "walk-in",
    title: finalCopy("How do I book a chat?"),
    body: finalCopy(
      "Just walk up and say hi, or check the mentor board to see who's in and book a slot on their profile."
    ),
  },
  {
    id: "conversation",
    title: finalCopy("What should we talk about?"),
    body: finalCopy(
      "Anything. Your project, your career, a technical wall you've hit, or what you're building after this!"
    ),
  },
];

const createPlaceholderMentors = (
  rotationId: string,
  count: number
): MentorCafePlaceholder[] =>
  Array.from({ length: count }, (_, index) => ({
    id: `${rotationId}-mentor-${index + 1}`,
    name: placeholderCopy("Mentor name"),
    title: placeholderCopy("Role @ Company"),
  }));

export const MENTOR_CAFE_ROTATIONS: MentorCafeRotation[] = [
  {
    id: "software-generalists",
    title: finalCopy("Software"),
    time: placeholderCopy("10:00 AM – 12:00 PM"),
    description: finalCopy(
      "Engineers from a range of industry backgrounds, covering full-stack development, machine learning, systems design, and deployment."
    ),
    mentors: createPlaceholderMentors("software-generalists", 8),
  },
  {
    id: "startups-vc",
    title: finalCopy("VC & Startups"),
    time: placeholderCopy("12:00 PM – 2:00 PM"),
    description: finalCopy(
      "Investors and startup operators offering feedback on pitches, business models, and paths to taking a project further."
    ),
    mentors: createPlaceholderMentors("startups-vc", 8),
  },
  {
    id: "design",
    title: finalCopy("Design"),
    time: placeholderCopy("2:00 PM – 4:00 PM"),
    description: finalCopy(
      "Designers offering critiques on visual design, usability, and user experience."
    ),
    mentors: createPlaceholderMentors("design", 4),
  },
  {
    id: "hardware",
    title: finalCopy("Hardware"),
    time: placeholderCopy("4:00 PM – 6:00 PM"),
    description: finalCopy(
      "Mentors with backgrounds in embedded systems, PCB design, and robotics, available for hands-on debugging and component-level questions."
    ),
    mentors: createPlaceholderMentors("hardware", 4),
  },
  {
    id: "mixed-open-overflow",
    title: placeholderCopy("Mixed / Open Overflow"),
    time: placeholderCopy("6:00 PM – 8:00 PM"),
    description: finalCopy(
      "A cozy, welcome space to meet our many talented and experienced mentors. Come by and say hi!"
    ),
    mentors: createPlaceholderMentors("mixed-open-overflow", 10),
  },
];
